/**
 * The pure half of the Online Training module player (2026-11-02): the
 * back/next path, where a topic sits on it, watched-progress kept in the
 * browser, and the small formatting the page needs. No React here, so all
 * of it is unit-tested directly (`module-player.test.ts`).
 */
import type { ModuleDetail, ModuleTopic, TopicVideo } from "./modules-api.js";

export const MODULES_PATH = "/trainings/modules";

export function modulePath(slug: string, topicId?: number): string {
  const base = `${MODULES_PATH}/${encodeURIComponent(slug)}`;
  return topicId === undefined ? base : `${base}?topic=${topicId}`;
}

/** Every topic, sections and lessons alike, in menu order. */
export function allTopics(mod: ModuleDetail): ModuleTopic[] {
  return mod.sections.flatMap((s) => [s, ...s.lessons]);
}

/** The topics the player walks — those with a video, in play order. The
 * server's `playlist` is the authority; topics it names that are missing are
 * skipped rather than crashing the page. */
export function playlistTopics(mod: ModuleDetail): ModuleTopic[] {
  const byId = new Map(allTopics(mod).map((t) => [t.id, t]));
  return mod.playlist.flatMap((id) => {
    const t = byId.get(id);
    return t ? [t] : [];
  });
}

export interface PlayerPosition {
  current: ModuleTopic | null;
  index: number; // 0-based on the playlist, -1 when nothing plays
  total: number;
  prev: ModuleTopic | null;
  next: ModuleTopic | null;
}

/** Where `topicId` sits on the path. An unknown or video-less id falls back
 * to `fallbackId` (where the viewer left off), then the first video. */
export function positionOf(mod: ModuleDetail, topicId: number | null, fallbackId: number | null = null): PlayerPosition {
  const list = playlistTopics(mod);
  let index = topicId === null ? -1 : list.findIndex((t) => t.id === topicId);
  if (index < 0 && fallbackId !== null) index = list.findIndex((t) => t.id === fallbackId);
  if (index < 0 && list.length > 0) index = 0;
  return {
    current: list[index] ?? null,
    index,
    total: list.length,
    prev: index > 0 ? list[index - 1] ?? null : null,
    next: index >= 0 && index < list.length - 1 ? list[index + 1] ?? null : null,
  };
}

/** The topic to open from a module card: the first video not yet watched,
 * else the first video. */
export function resumeTopic(mod: ModuleDetail, watched: ReadonlySet<number>): ModuleTopic | null {
  const list = playlistTopics(mod);
  return list.find((t) => !watched.has(t.id)) ?? list[0] ?? null;
}

/** Share of the path watched, 0–1. */
export function progressOf(watched: ReadonlySet<number> | readonly number[], total: number): number {
  const n = Array.isArray(watched) ? watched.length : (watched as ReadonlySet<number>).size;
  if (total <= 0) return 0;
  return Math.min(1, n / total);
}

// ── Watched progress, kept in this browser ─────────────────────────────
//
// A per-viewer convenience: which videos this person has finished. It lives
// in localStorage because the reseller namespace of portal-api is read-only
// by design; losing it (a private window, cleared site data) costs nothing
// but the check marks.

const STORAGE_PREFIX = "xop.training.watched.";

export function readWatched(slug: string): Set<number> {
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + slug);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((x): x is number => typeof x === "number") : []);
  } catch {
    return new Set();
  }
}

export function writeWatched(slug: string, watched: ReadonlySet<number>): void {
  try {
    window.localStorage.setItem(STORAGE_PREFIX + slug, JSON.stringify([...watched]));
  } catch {
    // Storage unavailable — progress simply is not remembered.
  }
}

// ── What to play ─────────────────────────────────────────────────────────

export type PlayableSource =
  | { type: "native"; src: string; contentType: string | null }
  | { type: "embed"; src: string }
  | { type: "external"; href: string }
  | { type: "none" };

/** How the stage plays a topic's video. A raw file (uploaded, or a direct
 * .mp4 / Dropbox link) plays in a native `<video>`; a hosted player is
 * framed; anything else opens on its own page. */
export function playableSource(video: TopicVideo | null): PlayableSource {
  if (!video) return { type: "none" };
  if (video.kind === "file") return { type: "native", src: video.src, contentType: video.content_type };
  if (video.direct_url) return { type: "native", src: video.direct_url, contentType: null };
  if (video.provider_url) return { type: "embed", src: video.provider_url };
  return { type: "external", href: video.url };
}

/** YouTube and Vimeo both start on `autoplay=1` — used once the viewer has
 * pressed play, so moving to the next topic keeps playing. */
export function withAutoplay(src: string): string {
  return src.includes("autoplay=") ? src : `${src}${src.includes("?") ? "&" : "?"}autoplay=1`;
}

/** `m:ss` / `h:mm:ss` for a topic. */
export function formatClock(seconds: number | null | undefined): string | null {
  if (!seconds || !Number.isFinite(seconds) || seconds <= 0) return null;
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  return h > 0 ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
}

/** "12 videos · 34 min" for a module card. */
export function moduleMeta(topicCount: number, totalSeconds: number): string {
  const videos = `${topicCount} video${topicCount === 1 ? "" : "s"}`;
  if (!totalSeconds || totalSeconds <= 0) return videos;
  const minutes = Math.max(1, Math.round(totalSeconds / 60));
  const length = minutes >= 60 ? `${Math.floor(minutes / 60)} h ${minutes % 60} min` : `${minutes} min`;
  return `${videos} · ${length}`;
}
